import React, { ChangeEvent, useState, KeyboardEvent, useEffect } from 'react'
import { Card, Form, Row, Col, Button, Collapse } from 'react-bootstrap'
import { PermissionEventFilters } from 'hooks/use-permission-events'
import { DEFAULT_PERMISSION_EVENTS_PAGE_SIZE } from 'constants/filters'

interface PermissionEventsFiltersProps {
  filters: PermissionEventFilters
  onFiltersChange: (filters: PermissionEventFilters) => void
  senderNumber: string
  loading?: boolean
}

const PermissionEventsFiltersComponent: React.FC<
  PermissionEventsFiltersProps
> = ({ filters, onFiltersChange, senderNumber, loading }) => {
  const [localRecipientNumber, setLocalRecipientNumber] = useState(
    filters.recipientNumber || ''
  )
  const [isCollapsed, setIsCollapsed] = useState(true)

  // Sync local state when filters change externally (e.g., clear filters)
  useEffect(() => {
    if (filters.recipientNumber !== undefined) {
      setLocalRecipientNumber(filters.recipientNumber)
    } else {
      setLocalRecipientNumber('')
    }
  }, [filters.recipientNumber])

  const handleRecipientKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleFilterChange('recipientNumber', localRecipientNumber)
    }
  }

  const handleFilterChange = (
    key: keyof PermissionEventFilters,
    value: any
  ) => {
    // Special handling for different types of values
    let processedValue = value

    // Handle boolean values explicitly
    if (key === 'finalDecision' || key === 'panopticonCanSend') {
      processedValue =
        value === '' || value === undefined || value === null
          ? undefined
          : value
    }
    // Handle string values (dates, recipient number)
    else if (
      key === 'startDate' ||
      key === 'endDate' ||
      key === 'recipientNumber'
    ) {
      processedValue = value === '' ? undefined : value
    }
    // Handle numeric values (limit)
    else if (key === 'limit') {
      processedValue = value
    }
    // Default handling
    else {
      processedValue = value === '' || value === null ? undefined : value
    }

    const newFilters = {
      ...filters,
      [key]: processedValue,
    }

    // Only reset cursor for actual filter changes, not for limit changes
    if (key !== 'limit') {
      newFilters.cursor = undefined
    }

    // Validate date order - if end date is before start date, clear end date
    if (
      key === 'startDate' &&
      newFilters.endDate &&
      value &&
      value > newFilters.endDate
    ) {
      newFilters.endDate = undefined
    }
    // If start date is after end date, clear start date
    if (
      key === 'endDate' &&
      newFilters.startDate &&
      value &&
      newFilters.startDate > value
    ) {
      newFilters.startDate = undefined
    }

    onFiltersChange(newFilters)
  }

  const clearFilters = () => {
    // Clear local recipient state immediately
    setLocalRecipientNumber('')

    // Clear all filters except page size
    onFiltersChange({
      limit: filters.limit || DEFAULT_PERMISSION_EVENTS_PAGE_SIZE,
    })
  }

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) =>
      key !== 'limit' && key !== 'cursor' && value !== undefined && value !== ''
  ).length

  return (
    <Card className="mb-4">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="d-flex align-items-center">
          <span className="me-2">{isCollapsed ? '▶' : '▼'}</span>
          <h5 className="mb-0 me-3">Filters</h5>
          {activeFiltersCount > 0 && (
            <span className="badge bg-secondary">
              {activeFiltersCount} active
            </span>
          )}
        </div>

        <div className="d-flex align-items-center">
          {activeFiltersCount > 0 && !isCollapsed && (
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                clearFilters()
              }}
              disabled={loading}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card.Header>

      <Collapse in={!isCollapsed}>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Linq User</Form.Label>
                <Form.Control
                  type="text"
                  value={senderNumber}
                  disabled
                  style={{
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'monospace',
                  }}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Recipient Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="+17043879366"
                  value={localRecipientNumber}
                  onChange={(e) => setLocalRecipientNumber(e.target.value)}
                  onKeyPress={handleRecipientKeyPress}
                  disabled={loading}
                  style={{ fontFamily: 'monospace' }}
                />
                <Form.Text className="text-muted">
                  Filter by specific recipient phone number (Press Enter to
                  search)
                </Form.Text>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Final Decision</Form.Label>
                <Form.Control
                  as="select"
                  value={filters.finalDecision?.toString() || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange(
                      'finalDecision',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  disabled={loading}
                >
                  <option value="">All Decisions</option>
                  <option value="true">Allowed</option>
                  <option value="false">Blocked</option>
                </Form.Control>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Panopticon Decision</Form.Label>
                <Form.Control
                  as="select"
                  value={filters.panopticonCanSend?.toString() || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange(
                      'panopticonCanSend',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  disabled={loading}
                >
                  <option value="">All Recommendations</option>
                  <option value="true">Allow</option>
                  <option value="false">Block</option>
                </Form.Control>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Results per Page</Form.Label>
                <Form.Control
                  as="select"
                  value={filters.limit || DEFAULT_PERMISSION_EVENTS_PAGE_SIZE}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange('limit', parseInt(e.target.value))
                  }
                  disabled={loading}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Control>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-bold">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate || ''}
                  max={filters.endDate || undefined}
                  onChange={(e) =>
                    handleFilterChange('startDate', e.target.value)
                  }
                  disabled={loading}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-bold">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate || ''}
                  min={filters.startDate || undefined}
                  onChange={(e) =>
                    handleFilterChange('endDate', e.target.value)
                  }
                  disabled={loading}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Collapse>
    </Card>
  )
}

export default PermissionEventsFiltersComponent
